import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { bedService, floorService, roomService, settingsService, tenantService } from '../services/api';
import { Bed, DailyGuest, Floor, Room } from '../types';
import './ConvertGuestToTenantModal.css';

function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function dateOnlyFromApi(v: string | undefined | null): string {
  if (!v) return '';
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : toYmd(d);
}

function suggestRentForBed(bed: Bed, room: Room | undefined): number {
  const fromBed = bed.rentPerMonth ?? bed.rentPerWeek ?? bed.rentPerDay;
  const fromRoom = room?.rentPerBed;
  const n = fromBed ?? fromRoom ?? 0;
  return Number.isFinite(Number(n)) ? Number(n) : 0;
}

type Props = {
  guest: DailyGuest | null;
  hostelId: number;
  onClose: () => void;
  onSuccess: () => void;
};

const ConvertGuestToTenantModal: React.FC<Props> = ({ guest, hostelId, onClose, onSuccess }) => {
  const [floors, setFloors] = useState<Floor[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [hasDefault, setHasDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    floorId: '' as string | number,
    roomId: '' as string | number,
    bedId: '' as string | number,
    rent: '',
    advance: '',
    dateOfJoining: '',
    paymentDate: toYmd(new Date()),
    note: '',
  });

  const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])) as Record<number, Room>, [rooms]);

  const loadLookups = useCallback(async () => {
    try {
      const [fl, rm, bd, defaults] = await Promise.all([
        floorService.getFloorsByHostel(hostelId),
        roomService.getRoomsByHostel(hostelId),
        bedService.getBedsByHostel(hostelId),
        settingsService.getTenantDefaults(hostelId),
      ]);
      setFloors(fl.filter((f) => f.isActive !== false));
      setRooms(rm.filter((r) => r.isActive !== false));
      setBeds(bd.filter((b) => b.isActive !== false));
      setHasDefault(defaults.hasDefault);
    } catch {
      setFloors([]);
      setRooms([]);
      setBeds([]);
      setHasDefault(false);
    }
  }, [hostelId]);

  useEffect(() => {
    void loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (!guest) return;
    const floorId = guest.floorId ?? '';
    const roomId = guest.roomId ?? '';
    const bedId = guest.bedId ?? '';
    const bed = bedId !== '' ? beds.find((b) => Number(b.id) === Number(bedId)) : undefined;
    const room = roomId !== '' ? roomById[Number(roomId)] : undefined;
    const suggestedRent = bed ? suggestRentForBed(bed, room) : Number(guest.dailyRate) > 0 ? Number(guest.dailyRate) * 30 : 0;
    setForm({
      floorId,
      roomId,
      bedId,
      rent: suggestedRent > 0 ? String(Math.round(suggestedRent)) : '',
      advance: guest.amountPaid != null && Number(guest.amountPaid) > 0 ? String(guest.amountPaid) : '',
      dateOfJoining: dateOnlyFromApi(guest.checkInDate) || toYmd(new Date()),
      paymentDate: toYmd(new Date()),
      note: guest.comments?.trim() || '',
    });
  }, [guest, beds, roomById]);

  const roomsOnFloor = useMemo(() => {
    if (form.floorId === '') return rooms;
    return rooms.filter((r) => Number(r.floorId) === Number(form.floorId));
  }, [rooms, form.floorId]);

  const bedsInRoom = useMemo(() => {
    if (form.roomId === '') return beds;
    return beds.filter((b) => Number(b.roomId) === Number(form.roomId));
  }, [beds, form.roomId]);

  const handleBedChange = (bedIdStr: string) => {
    if (bedIdStr === '') {
      setForm((p) => ({ ...p, bedId: '', rent: '' }));
      return;
    }
    const bid = Number(bedIdStr);
    const bed = beds.find((b) => b.id === bid);
    const room = bed ? roomById[Number(bed.roomId)] : undefined;
    const suggested = bed ? suggestRentForBed(bed, room) : 0;
    setForm((p) => ({
      ...p,
      bedId: bid,
      rent: suggested > 0 ? String(Math.round(suggested)) : p.rent,
    }));
  };

  const submit = async () => {
    if (!guest?.id) return;
    if (!hasDefault) {
      toast.error('Set a default tenant password in Settings before converting guests.');
      return;
    }
    if (form.bedId === '') {
      toast.error('Select a bed for the tenant.');
      return;
    }
    if (!form.dateOfJoining.trim() || !form.paymentDate.trim()) {
      toast.error('Date of joining and payment date are required.');
      return;
    }
    setSaving(true);
    try {
      const res = await tenantService.convertDailyGuestToTenant(guest.id, {
        bedId: Number(form.bedId),
        rent: Number(form.rent) || 0,
        advance: Number(form.advance) || 0,
        dateOfJoining: form.dateOfJoining,
        paymentDate: form.paymentDate,
        note: form.note.trim() || undefined,
      });
      toast.success(res.message || 'Guest converted to tenant');
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not convert guest to tenant');
    } finally {
      setSaving(false);
    }
  };

  if (!guest) return null;

  return (
    <div className="cvt-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="cvt-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="cvt-modal__head">
          <div>
            <h3>Convert to tenant</h3>
            <p className="cvt-modal__sub">
              Turn <strong>{guest.name}</strong> into a monthly tenant. The daily guest stay will be marked checked out.
            </p>
          </div>
          <button type="button" className="cvt-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {!hasDefault ? (
          <p className="cvt-modal__warn">
            Set a default tenant password under <strong>Settings</strong> before converting guests.
          </p>
        ) : null}

        <div className="cvt-modal__grid">
          <label>
            Floor
            <select
              value={String(form.floorId)}
              onChange={(e) =>
                setForm((p) => ({ ...p, floorId: e.target.value, roomId: '', bedId: '' }))
              }
            >
              <option value="">—</option>
              {floors.map((f) => (
                <option key={f.id} value={f.id}>
                  {String(f.floorNumber ?? f.id)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Room
            <select
              value={String(form.roomId)}
              onChange={(e) => setForm((p) => ({ ...p, roomId: e.target.value, bedId: '' }))}
            >
              <option value="">—</option>
              {roomsOnFloor.map((r) => (
                <option key={r.id} value={r.id}>
                  {String(r.roomNumber ?? r.id)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Bed *
            <select value={String(form.bedId)} onChange={(e) => handleBedChange(e.target.value)}>
              <option value="">—</option>
              {bedsInRoom.map((b) => (
                <option key={b.id} value={b.id}>
                  {String(b.bedNumber ?? b.bedName ?? b.id)}
                  {b.bedStatus === 'AVAILABLE' ? '' : ' (occupied)'}
                </option>
              ))}
            </select>
          </label>
          <label>
            Monthly rent
            <input
              type="number"
              min={0}
              value={form.rent}
              onChange={(e) => setForm((p) => ({ ...p, rent: e.target.value }))}
            />
          </label>
          <label>
            Advance
            <input
              type="number"
              min={0}
              value={form.advance}
              onChange={(e) => setForm((p) => ({ ...p, advance: e.target.value }))}
            />
          </label>
          <label>
            Date of joining *
            <input
              type="date"
              value={form.dateOfJoining}
              onChange={(e) => setForm((p) => ({ ...p, dateOfJoining: e.target.value }))}
            />
          </label>
          <label>
            Payment date *
            <input
              type="date"
              value={form.paymentDate}
              onChange={(e) => setForm((p) => ({ ...p, paymentDate: e.target.value }))}
            />
          </label>
          <label className="cvt-modal__note">
            Note
            <textarea
              value={form.note}
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
              placeholder="Optional — copied to tenant profile"
            />
          </label>
        </div>

        <div className="cvt-modal__actions">
          <button type="button" className="cvt-modal__btn cvt-modal__btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="cvt-modal__btn cvt-modal__btn--primary"
            onClick={() => void submit()}
            disabled={saving || !hasDefault}
          >
            {saving ? 'Converting…' : 'Convert to tenant'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConvertGuestToTenantModal;
